import { EquipmentCondition } from '../../../core/equipment-ad.service'
import { fileUrl } from '../../../shared/file-url'

/* ── Persian labels for the M3 equipment discovery surfaces (PRD فاز۳) ────── */

/** وضعیت کالا — labels from the M3 contract (list filters + cards + details). */
export const CONDITION_OPTIONS: { label: string; value: EquipmentCondition }[] = [
	{ label: 'نو و پلمب', value: 'SEALED_NEW' },
	{ label: 'نو بدون پلمب', value: 'NEW_UNSEALED' },
	{ label: 'در حد نو', value: 'LIKE_NEW' },
	{ label: 'کارکرده سالم', value: 'USED_HEALTHY' },
	{ label: 'کارکرده با ایراد جزئی', value: 'USED_MINOR_FLAW' },
	{ label: 'نیازمند تعمیر', value: 'NEEDS_REPAIR' },
	{ label: 'استوک', value: 'STOCK' },
	{ label: 'نمایشگاهی', value: 'SHOWCASE' },
]

export function conditionLabel(condition?: string | null): string {
	return CONDITION_OPTIONS.find(c => c.value === condition)?.label ?? condition ?? ''
}

/** Only flawed conditions expose the flaw description (PRD ch.13). */
export function hasFlaw(condition?: string | null): boolean {
	return condition === 'USED_MINOR_FLAW' || condition === 'NEEDS_REPAIR'
}

export const SALE_KIND_LABELS: Record<string, string> = {
	SINGLE: 'تک',
	WHOLESALE: 'عمده',
	BOTH: 'تک و عمده',
}

export const SPECIAL_USE_LABELS: Record<string, string> = {
	GENERAL: 'استفاده عمومی',
	SPECIALIZED: 'استفاده تخصصی',
	TRAINED_OPERATOR: 'نیاز به اپراتور آموزش‌دیده',
	PHYSICIAN_SUPERVISION: 'نیاز به نظارت پزشک',
	LICENSE_REQUIRED: 'نیاز به مجوز',
	UNSURE: 'مطمئن نیستم',
}

export const AUTHENTICITY_LABELS: Record<string, string> = {
	ORIGINAL_DECLARED: 'اصل — اعلام‌شده توسط فروشنده',
	DOMESTIC: 'تولید داخل',
	IMPORTED: 'وارداتی',
	UNKNOWN: 'نامشخص',
}

export const SWAP_MODE_LABELS: Record<string, string> = {
	NONE: 'فقط فروش',
	ONLY_SWAP: 'فقط معاوضه',
	SALE_OR_SWAP: 'فروش یا معاوضه',
}

export const DELIVERY_METHOD_LABELS: Record<string, string> = {
	IN_PERSON: 'تحویل حضوری',
	COURIER: 'پیک',
	POST: 'پست',
	TIPAX: 'تیپاکس',
	FREIGHT: 'باربری',
	SELLER_SHIPPING: 'ارسال توسط فروشنده',
	OTHER: 'سایر',
}

export const SHIPPING_BEARER_LABELS: Record<string, string> = {
	FREE: 'رایگان',
	BUYER: 'با خریدار',
	SELLER: 'با فروشنده',
	NEGOTIATED: 'توافقی',
	BY_DESTINATION: 'بر اساس مقصد',
}

export const RETURN_BEARER_LABELS: Record<string, string> = {
	BUYER: 'خریدار',
	SELLER: 'فروشنده',
	CASE_BY_CASE: 'موردی / توافقی',
}

/** Persian-digit price in Toman (courses-compare pattern). */
export function formatToman(value?: number | null): string {
	return value == null ? '—' : `${value.toLocaleString('fa-IR')} تومان`
}

/** تاریخ شمسی با ارقام فارسی — «۱۴ آبان ۱۴۰۴» (fallback متن خام در خطا). */
export function faDate(iso?: string | null): string {
	if (!iso) return '—'
	try {
		const d = new Date(iso)
		return isNaN(d.getTime()) ? iso : d.toLocaleDateString('fa-IR')
	} catch {
		return iso
	}
}

/** عدد با ارقام فارسی — «۴.۵». */
export function faNum(value?: number | null, fractionDigits = 1): string {
	if (value == null) return '—'
	return value.toLocaleString('fa-IR', { maximumFractionDigits: fractionDigits })
}

/**
 * mediaUrls may carry either plain file ids or absolute URLs — resolve to a
 * stable public URL, else null (caller falls back to the local placeholder).
 */
export function equipmentMediaUrl(raw?: string | null): string | null {
	if (!raw) return null
	return /^https?:/i.test(raw) ? raw : fileUrl(raw)
}

/**
 * Cover of a search/listing doc: the elastic index exposes media as `fileId[]`
 * (the detail endpoint uses `mediaUrls`), so check every shape the API returns.
 */
export function equipmentDocCoverUrl(doc: {
	fileId?: string[] | null
	mediaUrls?: string[] | null
	mediaFileUrls?: string[] | null
	thumbnailImage?: string | null
}): string | null {
	return equipmentMediaUrl(doc.fileId?.[0] ?? doc.mediaUrls?.[0] ?? doc.mediaFileUrls?.[0] ?? doc.thumbnailImage)
}
